import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import type { RefObject } from 'react';

import { ChartActionMenuDropdown } from '../../CustomDash/components/Chart/chart-menu/ChartActionMenuDropdown';
import { type DiiRow, diiTotal, StackedDiiBars } from './StackedDiiBars';

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const csvCell = (value: string | number): string => {
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (rows: DiiRow[], labelHead: string, filename: string) => {
  const header = [labelHead, 'Direct', 'Indirect', 'Induced', 'Total'];
  const lines = rows.map((r) => [
    r.label,
    Math.round(r.direct),
    Math.round(r.indirect),
    Math.round(r.induced),
    Math.round(diiTotal(r)),
  ]);
  const csv = [header, ...lines]
    .map((row) => row.map(csvCell).join(','))
    .join('\n');
  downloadBlob(
    new Blob([csv], { type: 'text/csv;charset=utf-8' }),
    `${filename}.csv`
  );
};

// html2canvas-pro + pptxgenjs are heavy; load them on demand so they stay
// out of the page's initial bundle.
const captureCanvas = async (el: HTMLElement) => {
  const { default: html2canvas } = await import('html2canvas-pro');
  return html2canvas(el, { backgroundColor: '#ffffff', scale: 2 });
};

const downloadPng = async (el: HTMLElement, filename: string) => {
  const canvas = await captureCanvas(el);
  canvas.toBlob((blob) => {
    if (blob) downloadBlob(blob, `${filename}.png`);
  });
};

const downloadPpt = async (
  el: HTMLElement,
  title: string,
  filename: string
) => {
  const [{ default: pptxgen }, canvas] = await Promise.all([
    import('pptxgenjs'),
    captureCanvas(el),
  ]);
  const pptx = new pptxgen();
  pptx.defineLayout({ name: 'WIDE', width: 10, height: 5.63 });
  pptx.layout = 'WIDE';
  const slide = pptx.addSlide();
  slide.addText(title, {
    x: 0.5,
    y: 0.3,
    w: 9,
    h: 0.5,
    fontSize: 18,
    bold: true,
    color: '2D426A',
  });
  const imgW = 9;
  const imgH = Math.min(4.4, (imgW * canvas.height) / canvas.width);
  slide.addImage({
    data: canvas.toDataURL('image/png'),
    x: 0.5,
    y: 1,
    w: imgW,
    h: imgH,
  });
  await pptx.writeFile({ fileName: `${filename}.pptx` });
};

interface ChartActionsProps {
  title: string;
  rows: DiiRow[];
  labelHead: string;
  labelWidth: number;
  /** Filename stem for the CSV / PNG / PPT downloads. */
  filename: string;
  /** Element captured for the PNG / PPT exports (the card). */
  captureRef: RefObject<HTMLElement>;
}

// The standard dashboard chart-action menu. Expand (modal), Download Data
// (CSV), Download PNG and Download PPT work standalone for these bespoke
// cards. Share is shown but disabled — sharing a plot needs the CustomDash
// share-token / saved-dashboard backend these cards aren't wired into.
export function ChartActions({
  title,
  rows,
  labelHead,
  labelWidth,
  filename,
  captureRef,
}: ChartActionsProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const noop = () => undefined;

  return (
    <>
      <ChartActionMenuDropdown
        menu={[
          'expand-plot',
          'share-plot',
          'download-data',
          'download-png',
          'download-ppt',
        ]}
        notSharableReason="Sharing isn’t available for economic-impact scenarios yet"
        onExpandPlotClick={onOpen}
        onSharePlotClick={noop}
        onPreviewPPTClick={noop}
        onDownloadDataClick={() => downloadCsv(rows, labelHead, filename)}
        onDownloadPNGClick={() => {
          if (captureRef.current) downloadPng(captureRef.current, filename);
        }}
        onDownloadPPTClick={() => {
          if (captureRef.current) {
            downloadPpt(captureRef.current, title, filename);
          }
        }}
      />
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
        <ModalOverlay />
        <ModalContent data-testid="expand-plot-modal">
          <ModalHeader fontSize="16px" color="navyBlue.500">
            {title}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <StackedDiiBars rows={rows} labelWidth={labelWidth} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
